/**
 * P2P network protocol: message types and state checksum.
 *
 * Lock-step deterministic sync: both peers run the full simulation (bots
 * included) and exchange only human inputs. Every action a human takes during
 * a turn is one sequenced `action` message; the receiver applies them in order
 * when its own game is ready for them (see NetworkSync + Game.pumpRemoteActions).
 */

import type { GameState, TurnInput, Vec3, MenuOptions } from '../types';

// --- Message variants ---

/** Sent by each peer right after the data channel opens. */
export interface NetHello {
  type: 'hello';
  wasm: boolean;   // this peer can run the WASM physics kernel
}

export interface NetGameConfig {
  type: 'gameConfig';
  options: MenuOptions;
  /** Host's decision: both peers use WASM only if both have it, otherwise both use JS. */
  useWasm: boolean;
}

export interface NetDeploySelection {
  type: 'deploySelection';
  teamIndex: number;
  planetIndex: number;
  surfaceDir: Vec3;
}

/** One human action (fire, move, jump, build, heal, shop, melee, interact, …). */
export interface NetAction {
  type: 'action';
  seq: number;          // per-sender, strictly increasing from 1
  turnNumber: number;
  playerIndex: number;
  input: TurnInput;
}

export interface NetChecksum {
  type: 'checksum';
  turnNumber: number;
  playerIndex: number;  // whose turn just ended
  hash: number;
}

export interface NetPing {
  type: 'ping';
  timestamp: number;
}

export interface NetPong {
  type: 'pong';
  timestamp: number;
}

export interface NetDisconnect {
  type: 'disconnect';
}

export type NetMessage =
  | NetHello
  | NetGameConfig
  | NetDeploySelection
  | NetAction
  | NetChecksum
  | NetPing
  | NetPong
  | NetDisconnect;

// --- Checksum ---

/**
 * Checksum of everything that must match between peers. FNV-1a over:
 * turn/phase, every player's combat + inventory state, every planet's full
 * heightmap and mass, and the world objects (structures, hazards, flags, cans,
 * carryables, ground items, fire zones, pending clusters, mode state).
 *
 * Objects are hashed through JSON — both peers build them with the same code,
 * so key order matches. Rendering-only fields are not in GameState.
 */
export function computeStateChecksum(state: GameState): number {
  let hash = 2166136261;
  const mixByte = (b: number) => { hash ^= b & 0xff; hash = Math.imul(hash, 16777619); };
  const mixInt = (v: number) => { mixByte(v); mixByte(v >> 8); mixByte(v >> 16); mixByte(v >> 24); };
  const f32 = new Float32Array(1);
  const u32 = new Uint32Array(f32.buffer);
  const mixF32 = (v: number) => { f32[0] = v; mixInt(u32[0]); };
  const mixNum = (v: number) => { mixInt(Math.round(v * 10000) | 0); };
  const mixStr = (s: string) => { for (let i = 0; i < s.length; i++) mixInt(s.charCodeAt(i)); };
  const mixJson = (o: unknown) => mixStr(JSON.stringify(o ?? null));

  mixInt(state.turnNumber);
  mixInt(state.currentPlayerIndex);
  mixStr(state.phase);

  for (const p of state.players) {
    mixInt(p.id);
    mixNum(p.health);
    mixInt(p.alive ? 1 : 0);
    mixInt(p.planetIndex);
    mixNum(p.surfaceDir.x); mixNum(p.surfaceDir.y); mixNum(p.surfaceDir.z);
    mixNum(p.cbp);
    mixNum(p.movementRemaining);
    mixNum(p.jumppackFuel);
    mixInt(p.kills);
    mixNum(p.damageDealt);
    mixInt(p.shotsFired);
    mixInt(p.airborne ? 1 : 0);
    mixInt(p.empTurnsRemaining ?? 0);
    mixInt(p.tetheredTurns ?? 0);
    mixInt(p.flagsRemaining);
    mixJson(Object.keys(p.loadout.ammo).sort().map(k => [k, (p.loadout.ammo as Record<string, number | null>)[k]]));
    mixJson(p.loadout.items ? Object.keys(p.loadout.items).sort().map(k => [k, (p.loadout.items as Record<string, number>)[k]]) : null);
    mixJson(p.pendingCommand ?? null);
  }

  for (const planet of state.planets) {
    mixInt(planet.id);
    mixNum(planet.mass);
    mixInt(planet.alive ? 1 : 0);
    const hm = planet.heightmap;
    for (let i = 0; i < hm.length; i++) mixF32(hm[i]);
  }

  mixJson(state.structures);
  mixJson(state.hazards);
  mixJson(state.plantedFlags);
  mixJson(state.crashedDropCans);
  mixJson(state.carryables);
  mixJson(state.groundItems);
  mixJson(state.collectibles ?? []);
  mixJson(state.fireZones ?? []);
  mixJson(state.pendingClusters);
  mixJson(state.modeState);
  mixJson(state.ruins.map(r => [r.id, (r as unknown as { claim?: unknown }).claim ?? null, (r as unknown as { interactable?: boolean }).interactable ?? null]));

  return hash >>> 0;
}
